
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';
import { createOrder, deleteCoupon } from '@eliadmin/core/src/api/cart/cart-api';
import { ShipmentType } from '@eliadmin/core/src/api/cart/cart-types';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { useConfirmCartDistanceDestination } from '@eliadmin/core/src/components/cart/detail/cart-distance-destination-dialog';
import { useProformAddrQueryOpts } from '@eliadmin/core/src/components/cart/detail/proform-addr-picker';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@eliadmin/core/src/components/ui/card';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CART_CONFIG } from '@eliadmin/core/src/constants/cart-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useCartPayments } from '@eliadmin/core/src/hooks/cart/use-cart-payments';
import { useCartShipments } from '@eliadmin/core/src/hooks/cart/use-cart-shipments';
import { CartStep, useCartStepper } from '@eliadmin/core/src/hooks/cart/use-cart-stepper';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { UploadContext, type CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { isNullish } from '@eliadmin/core/src/utils/misc-utils';
import { replaceCartShipmentAddressValues } from '@eliadmin/core/src/utils/cart-shipment-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { extractProformArray } from '@eliadmin/core/src/utils/proform-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useFormContext, type FieldErrors, type FieldPath } from 'react-hook-form';
import { LuX } from 'react-icons/lu';

export type CartSummaryCardProps = {
  className?: string;
  itemsDisplayType?: 'list' | 'total';
  showContinueButton?: boolean;
  showSubmitButton?: boolean;
  usePaymentName?: boolean;
  useShipmentName?: boolean;
};

const AUTO_FILLED_ORDER_FIELDS: FieldPath<CreateOrderFormValues>[] = [
  'name',
  'surname',
  'email',
  'phone',
  'address',
  'street_number',
  'city',
  'postcode',
  'country',
  'company_buy',
  'company_name',
  'ico',
  'dic',
  'ic_dph',
  'different_delivery_address',
  'address_delivery',
  'address2',
  'street_number2',
  'city2',
  'postcode2',
  'country2',
];

const CART_CHECKOUT_MESSAGES = {
  sk: {
    missingBillingAddress: 'Vyberte prosím fakturačnú adresu.',
    missingPayment: 'Vyberte prosím spôsob platby.',
    missingShipment: 'Vyberte prosím spôsob dopravy.',
    incompleteShipment: 'Doplňte prosím údaje k zvolenému spôsobu dopravy.',
    title: 'Doplňte prosím údaje v košíku',
  },
  cs: {
    missingBillingAddress: 'Vyberte prosím fakturační adresu.',
    missingPayment: 'Vyberte prosím způsob platby.',
    missingShipment: 'Vyberte prosím způsob dopravy.',
    incompleteShipment: 'Doplňte prosím údaje ke zvolenému způsobu dopravy.',
    title: 'Doplňte prosím údaje v košíku',
  },
  en: {
    missingBillingAddress: 'Please select a billing address.',
    missingPayment: 'Please select a payment method.',
    missingShipment: 'Please select a shipping method.',
    incompleteShipment: 'Please complete the details for the selected shipping method.',
    title: 'Please complete the cart details',
  },
};

function getCartCheckoutMessages(locale: string) {
  return (
    CART_CHECKOUT_MESSAGES[locale as keyof typeof CART_CHECKOUT_MESSAGES] ??
    CART_CHECKOUT_MESSAGES.cs
  );
}

function getPayloadErrorMessages(payload: unknown) {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const messages: string[] = [];
  const seen = new WeakSet<object>();

  const visit = (value: unknown) => {
    if (!value) {
      return;
    }

    if (typeof value === 'string') {
      if (value.trim()) {
        messages.push(value);
      }
      return;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        visit(item);
      }
      return;
    }

    if (typeof value !== 'object') {
      return;
    }

    if (seen.has(value)) {
      return;
    }

    seen.add(value);

    if ('message' in value) {
      visit(value.message);
    }

    if ('errors' in value) {
      visit(value.errors);
    }

    if ('KARAT' in value) {
      visit(value.KARAT);
    }

    for (const [key, nestedValue] of Object.entries(value)) {
      if (key === 'message' || key === 'errors' || key === 'KARAT') {
        continue;
      }

      visit(nestedValue);
    }
  };

  visit(payload);

  return Array.from(new Set(messages));
}

function getApiErrorMessage(error: unknown) {
  const payload =
    error && typeof error === 'object' && 'payload' in error
      ? (error as { payload?: unknown }).payload
      : null;

  const payloadMessages = getPayloadErrorMessages(payload);
  if (payloadMessages.length) {
    return payloadMessages.join('\n');
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return null;
}

function getFriendlyCheckoutMessagesFromFields(
  fields: Record<string, unknown>,
  checkoutMessages: ReturnType<typeof getCartCheckoutMessages>,
) {
  const messages: string[] = [];
  const fieldNames = Object.keys(fields);

  const hasField = (patterns: string[]) =>
    fieldNames.some(field => patterns.some(pattern => field.includes(pattern)));

  if (hasField(['address_billing', 'billing_address', 'cart.address'])) {
    messages.push(checkoutMessages.missingBillingAddress);
  }

  if (hasField(['payment', 'paymentType', 'payment_type'])) {
    messages.push(checkoutMessages.missingPayment);
  }

  if (hasField(['shipment.complete', 'shipment.settings'])) {
    messages.push(checkoutMessages.incompleteShipment);
  } else if (hasField(['shipment'])) {
    messages.push(checkoutMessages.missingShipment);
  }

  return Array.from(new Set(messages));
}

function collectFormErrorMessages(errors: FieldErrors<CreateOrderFormValues>) {
  const messages: string[] = [];
  const seen = new WeakSet<object>();

  const visit = (value: unknown) => {
    if (!value || typeof value !== 'object') {
      return;
    }

    if (seen.has(value)) {
      return;
    }

    seen.add(value);

    if ('message' in value && typeof value.message === 'string' && value.message.trim()) {
      messages.push(value.message);
    }

    for (const [key, nested] of Object.entries(value)) {
      if (key === 'ref') {
        continue;
      }

      visit(nested);
    }
  };

  visit(errors);

  return Array.from(new Set(messages));
}

export function CartSummaryCard({
  className,
  itemsDisplayType = 'total',
  showContinueButton,
  showSubmitButton,
  usePaymentName = true,
  useShipmentName = true,
}: CartSummaryCardProps) {
  const { data: cart } = useCart() as any;
  const { data: payments } = useCartPayments();
  const { data: shipments } = useCartShipments();
  const form = useFormContext<CreateOrderFormValues>();
  const confirmDistanceDestination = useConfirmCartDistanceDestination();
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const heureka_connected = !!generalLayout.marketing?.heureka_customer_verification_id;

  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const checkoutMessages = useMemo(() => getCartCheckoutMessages(locale), [locale]);
  const currencySettings = useGlobalStore(s => s.generalLayout.currency_settings);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const me = useCustomerStore(s => s.me);

  const addrQuery = useQuery(useProformAddrQueryOpts());

  const searchParams = useSearchParams();

  const activePaymentId = cart.paymentType?.id;
  const activeShipmentId = cart.shipment?.id;

  const isDemandRef = useRef(false);

  const queryClient = useQueryClient();
  const createOrderMutation = useMutation({
    meta: { affectsCart: true },
mutationFn: createOrder });
  const deleteCouponMutation = useMutation({
    meta: { affectsCart: true },
mutationFn: deleteCoupon,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.CART] });
    },
  });

  const { attachments } = useContext(UploadContext);
  const hasPendingAttachmentUploads = attachments.some(
    attachment => attachment.status === 'uploading',
  );
  const hasFailedAttachmentUploads = attachments.some(attachment => attachment.status === 'error');

  useEffect(() => {
    const addrItem = extractProformArray(
      addrQuery.data?.root.inv_address_items?.inv_address_item,
    ).find(item => String(item.poradi) === String(cart.address));

    if (!addrItem) {
      return;
    }

    form.setValue('address_billing', Number(addrItem.poradi));
    form.setValue('address', addrItem.ulice);
    form.setValue('city', addrItem.mesto);
    form.setValue('street_number', addrItem.cislo_popisne);
    form.setValue('postcode', addrItem.psc);
    form.setValue('country', { id: addrItem.stat });
    form.setValue(
      'phone',
      addrItem.telefon
        ? `${addrItem.telefon_preset} ${addrItem.telefon}`
        : addrItem.mobil
          ? `${addrItem.mobil_preset} ${addrItem.mobil}`
          : '',
    );
    form.setValue('email', addrItem.e_mail);
    form.setValue('name', addrItem.kontaktni_osoba);
    form.setValue('surname', addrItem.kontaktni_osoba_p);
    if (addrItem.ico) form.setValue('ico', addrItem.ico);
    if (addrItem.dic) form.setValue('dic', addrItem.dic);
    if (addrItem.ic_dph) form.setValue('ic_dph', addrItem.ic_dph);
    if (addrItem.ico || addrItem.dic || addrItem.ic_dph) {
      form.setValue('company_buy', true);
      form.setValue('company_name', addrItem.ico || addrItem.dic || addrItem.ic_dph);
    }

    if (cart.delivery_address === null) {
      form.setValue('different_delivery_address', false);
      form.setValue('address_delivery', undefined);
      form.setValue('address2', '');
      form.setValue('street_number2', '');
      form.setValue('city2', '');
      form.setValue('postcode2', '');
      form.setValue('country2', { id: '' });
    } else {
      form.setValue('different_delivery_address', true);
      const addrItem = extractProformArray(
        addrQuery.data?.root.del_address_items?.del_address_item,
      ).find(item => String(item.poradi) === String(cart.delivery_address));

      if (addrItem) {
        form.setValue('address_delivery', Number(addrItem.poradi));
        form.setValue('address2', addrItem.ulice);
        form.setValue('city2', addrItem.mesto);
        form.setValue('street_number2', addrItem.cislo_popisne);
        form.setValue('postcode2', addrItem.psc);
        form.setValue('country2', { id: addrItem.stat });
      }
    }

    form.trigger(AUTO_FILLED_ORDER_FIELDS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.delivery_address, cart.address, addrQuery.data]);

  const activePayment = useMemo(() => {
    return payments?.find(payment => String(payment.id) === String(activePaymentId)) ?? null;
  }, [activePaymentId, payments]);

  const activeShipment = useMemo(() => {
    if (isNullish(activeShipmentId)) return null;

    return shipments?.find(shipment => String(shipment.id) === String(activeShipmentId)) ?? null;
  }, [activeShipmentId, shipments]);

  const totalPrice = useMemo(() => {
    const cartTotal = cart.total;

    // if (!activePayment || !activeShipment) return cartTotal;

    // TODO:
    // if (activePayment.price_with_vat) {
    //   return cartTotal + activePayment.price_with_vat;
    // }

    // if (activeShipment.price_with_vat) {
    //   return cartTotal + activeShipment.price_with_vat;
    // }

    return cartTotal;
  }, [cart.total]);

  const totalPriceWithoutVat = useMemo(() => {
    return cart.total_without_vat;
  }, [cart.total_without_vat]);

  const priceItems = useMemo(() => {
    const result: { label: string; value: string }[] = [];

    if (itemsDisplayType === 'total') {
      result.push({
        label: t('text1450'),
        value: formatPrice(cart.items_total_without_vat, currency, currencySettings, true),
      });
    }

    if (activePayment) {
      result.push({
        label:
          (usePaymentName ? `${t('text158')}: ${activePayment.name}` : t('text344')) +
          ` (${t('text443')})`,
        // value: formatPrice(roundPrice(activePayment.price), currency),
        value: cart.payment_type_total_without_vat
          ? formatPrice(cart.payment_type_total_without_vat, currency, currencySettings, true)
          : t('text349'),
      });
    }

    if (activeShipment) {
      result.push({
        label:
          (useShipmentName ? `${t('text153')}: ${activeShipment.name}` : t('text345')) +
          ` (${t('text443')})`,
        value: cart.shipment_total_without_vat
          ? formatPrice(cart.shipment_total_without_vat, currency, currencySettings, true)
          : t('text349'),
      });
    }

    return result;
  }, [
    activePayment,
    activeShipment,
    cart.items_total_without_vat,
    cart.payment_type_total_without_vat,
    cart.shipment_total_without_vat,
    currency,
    currencySettings,
    itemsDisplayType,
    t,
    usePaymentName,
    useShipmentName,
  ]);

  const markAnalytics = () => {
    if (window.dataLayer) {
      window.dataLayer.push({ ec: null });
      window.dataLayer.push({
        event: 'purchase',
        ec: {
          // transaction_id: {{ $order->id }},
          affiliation: 'Online Store',
          value: cart.total,
          // tax: {{ $order->getVat() }},
          currencyCode: currency,
          items: (cart.items || []).map((item: any) => ({
            id: item.id,
            name: item.name,
            // brand: '{{ $product->manufacturerName() }}',
            // category: '{{ $category ? $category->name : "" }}',
            price: item.price,
            quantity: item.qty,
          })),
        },
      });
    }
  };

  const isShipmentFulfilled = useMemo(() => {
    if (!activeShipmentId) {
      return false;
    }

    const shipment = shipments?.find(shipment => String(shipment.id) === String(activeShipmentId));
    if (!shipment || shipment.complete === false || shipment.settings?.complete === false) {
      return false;
    }

    if (
      shipment.type === ShipmentType.CUSTOM_PLACE &&
      (!shipment.settings?.place_of_delivery || !shipment.settings?.time_of_delivery)
    ) {
      return false;
    }

    if (
      (shipment.type === ShipmentType.PACKETA && !shipment.settings?.branch_id) ||
      (shipment.type === ShipmentType.SPS_ZBOX && !shipment.settings?.branch_id) ||
      (shipment.type === ShipmentType.GLS_ZBOX && !shipment.settings?.branch_id)
    ) {
      return false;
    }

    if (
      shipment.type === ShipmentType.TOP_TRANS &&
      (!shipment.settings?.city || !shipment.settings?.postcode)
    ) {
      return false;
    }

    return true;
  }, [activeShipmentId, shipments]);

  const getMissingCheckoutMessages = () => {
    const messages: string[] = [];

    if (isNullish(cart.address)) {
      messages.push(checkoutMessages.missingBillingAddress);
    }

    if (isNullish(activeShipmentId)) {
      messages.push(checkoutMessages.missingShipment);
    } else if (!isShipmentFulfilled) {
      messages.push(checkoutMessages.incompleteShipment);
    }

    if (isNullish(activePaymentId)) {
      messages.push(checkoutMessages.missingPayment);
    }

    return messages;
  };

  useEffect(function checkStepValidityOnMount() {
    const cartStep = searchParams.get('cs');

    if (cartStep !== CartStep.ORDER) return;

    if (!isShipmentFulfilled) {
      setStep(CartStep.DETAIL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = form.handleSubmit(
    async data => {
      if (!(await confirmDistanceDestination())) return;
      replaceCartShipmentAddressValues(data, form.getValues());

      data.return_url = new URL(ROUTES.ORDER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString();

      if (hasPendingAttachmentUploads) {
        toast({
          title: t('text594'),
          description: 'Pockajte prosim, kym sa nahraju vsetky prilohy.',
          variant: 'destructive',
          duration: 100000,
        });
        return;
      }

      if (hasFailedAttachmentUploads) {
        toast({
          title: t('text594'),
          description:
            'Nie vsetky prilohy sa podarilo nahrat. Odstrante ich alebo ich nahrajte znova.',
          variant: 'destructive',
          duration: 100000,
        });
        return;
      }

      data.attachments = attachments
        .filter(attachment => attachment.status === 'uploaded' && attachment.id)
        .map(attachment => ({ id: attachment.id! }));

      if (!data.different_delivery_address) {
        data.different_delivery_address = false;
      }

      const orderPayload = {
        ...data,
        // send_heureka_mail: heureka_connected ? !data.send_heureka_mail : undefined,
        demand: isDemandRef.current,
      };

      try {
        const res = await createOrderMutation.mutateAsync({
          locale,
          body: orderPayload,
          authToken: me?.authToken,
        });

        markAnalytics();
        window.location.href = res?.redirect ?? '/';
      } catch (e) {
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV === 'development') console.log(e);

        if (e instanceof ValidationError) {
          for (const [field, errors] of Object.entries(e.fields)) {
            if (Array.isArray(errors)) {
              form.setError(field as any, { type: 'manual', message: errors.join('\n') });
            }
          }

          const friendlyMessages = getFriendlyCheckoutMessagesFromFields(
            e.fields,
            checkoutMessages,
          );
          const messages = Array.from(
            new Set([...getPayloadErrorMessages(e.fields), ...friendlyMessages]),
          );
          toast({
            title: checkoutMessages.title,
            description: messages.length ? messages.join('\n') : t('text1472'),
            variant: 'destructive',
            duration: 100000,
          });
          return;
        }

        toast({
          title: t('text1472'),
          description: getApiErrorMessage(e) ?? t('text1472'),
          variant: 'destructive',
          duration: 100000,
        });
      }
    },
    errors => {
      const messages = collectFormErrorMessages(errors);

      toast({
        title: t('text1472'),
        description: messages.length ? messages.join('\n') : t('text1472'),
        variant: 'destructive',
        duration: 100000,
      });
    },
  );

  const { setStep } = useCartStepper();

  const handleContinue = async () => {
    if (!(await confirmDistanceDestination())) return;

    const messages = getMissingCheckoutMessages();

    if (messages.length) {
      toast({
        title: checkoutMessages.title,
        description: messages.join('\n'),
        variant: 'destructive',
        duration: 100000,
      });
      return;
    }

    setStep(CartStep.ORDER);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  return (
    <Card variant="secondary" className={cn(className)}>
<CaptchaFormMessage errors={form.formState.errors} />
      <CardHeader className="p-4"></CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* list items */}
        {/* {itemsDisplayType === 'list' && (
          <div className="flex flex-col gap-2">
            {cart?.items?.map(item => (
              <div className="grid grid-cols-12 gap-2" key={item.id}>
                <span className="col-span-5 line-clamp-1 text-ellipsis text-sm font-bold">
                  {item.name}
                </span>
                <span className="col-span-2 text-right text-sm text-muted-foreground">
                  {item.qty} {item.unit}
                </span>
                <span className="col-span-3 text-right text-sm text-muted-foreground">
                  {formatPrice(item.price, currency, currencySettings)} / {item.unit}
                </span>
                <span className="col-span-2 text-right text-sm">
                  {formatPrice(item.price_subtotal, currency, currencySettings)}
                </span>
              </div>
            ))}
            <Separator />
          </div>
        )} */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-row justify-between">
            {/* <span className="text-sm">{t('text675')}</span> */}

            {/* <span className="text-sm font-bold" aria-label={t('text675')}>
              {formatPrice(totalPrice, currency, currencySettings)}
            </span> */}
          </div>
          {priceItems.map(item => (
            <div className="flex flex-row justify-between" key={`${item.label}-${item.value}`}>
              <span className="text-sm">{item.label}</span>

              <span className="whitespace-nowrap text-sm font-bold" aria-label={item.label}>
                {item.value}
              </span>
            </div>
          ))}
          {cart.coupons?.map((coupon: any) => (
            <div className="flex flex-row items-center justify-between" key={'coupon.code'}>
              <span className="flex flex-row items-center gap-2 text-sm">
                {t('text167')}
                <Button
                  size="icon-xs"
                  variant="ghost"
                  className="text-destructive"
                  isLoading={deleteCouponMutation.isPending}
                  onClick={() =>
                    deleteCouponMutation.mutate({
                      locale,
                      body: { coupon: coupon.code },
                      authToken: me?.authToken,
                    })
                  }
                >
                  <LuX />
                </Button>
              </span>

              <span className="text-sm font-bold" aria-label={t('text167')}>
                - {formatPrice(coupon.discounted, currency, currencySettings)}{' '}
                <span className="text-success-foreground">{'ZLAVA10'}</span>
              </span>
            </div>
          ))}
          <Separator />
        </div>
        {/* total */}
        <div className="flex flex-row justify-between">
          <span className="text-lg font-bold" id="together-price-without-vat">
            {t('text1451')}
          </span>
          <strong className="text-lg font-bold" aria-label={t('text1451')}>
            {formatPrice(totalPriceWithoutVat, currency, currencySettings)}
          </strong>
        </div>
        <div className="flex flex-row justify-between">
          <span className="text-lg font-bold" id="together-price">
            {t('text132')}
          </span>
          <strong className="text-lg font-bold" aria-label={t('text132')}>
            {formatPrice(totalPrice, currency, currencySettings)}
          </strong>
        </div>
      </CardContent>
      {showContinueButton && (
        <CardFooter>
          <Button onClick={handleContinue} className="w-full">
            {t('text526')}
          </Button>
        </CardFooter>
      )}

      {showSubmitButton && (
        <CardFooter className="flex flex-col gap-2">
          <Button
            className="w-full"
            variant={CART_CONFIG.cartSummaryCard.submitOrderButton.variant}
            aria-label={t('text168')}
            isLoading={form.formState.isSubmitting || hasPendingAttachmentUploads}
            onClick={e => {
              isDemandRef.current = false;
              handleSubmit(e);
            }}
          >
            {t('text168')}
          </Button>
          <Button
            className="w-full border-primary text-primary hover:border-primary hover:bg-primary/10 hover:text-primary"
            variant={'outline'}
            aria-label={t('text168')}
            isLoading={form.formState.isSubmitting || hasPendingAttachmentUploads}
            onClick={e => {
              isDemandRef.current = true;
              handleSubmit(e);
            }}
          >
            {t('text1118')}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
}
